import React, { useEffect, useState, useMemo } from "react";
import {
    Box, Card, Typography, TextField, Button, Table, TableHead, TableRow,
    TableCell, TableBody, IconButton, Chip, Grid, Select, MenuItem,
    InputLabel, FormControl, Pagination, Avatar, InputAdornment, Paper, Stack, Tooltip
} from "@mui/material";
import {
    EditOutlined, DeleteOutline, PersonAddAlt1, Search,
    Badge, Email, Phone, Lock, Group
} from "@mui/icons-material";
import {
    collection, getDocs, doc, setDoc, deleteDoc, query, where, Timestamp,
} from "firebase/firestore";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

const PAGE_SIZE = 10; // Increased per page since we are using high-density rows
const ROLE_OPTIONS = ["Admin", "Reception", "Accountant", "Library", "Teacher", "Store", "Warden", "Canteen", "Parent", "Driver", "Staff", "Security", "Other"];

function Usersmgmt() {
    const auth = getAuth();
    const [users, setUsers] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [schoolId, setSchoolId] = useState(null);
    const [roleFilter, setRoleFilter] = useState("All");

    const [form, setForm] = useState({
        name: "", email: "", mobile: "", password: "", role: "Teacher", status: "Active",
    });

    useEffect(() => {
        const fetchSchoolId = async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) return;
            const q = query(collection(db, "users"), where("uid", "==", currentUser.uid));
            const snap = await getDocs(q);
            if (!snap.empty) setSchoolId(snap.docs[0].data().schoolId);
        };
        fetchSchoolId();
    }, [auth]);

    useEffect(() => {
        if (schoolId) loadUsers();
    }, [schoolId]);

    const loadUsers = async () => {
        const q = query(collection(db, "users"), where("schoolId", "==", schoolId));
        const snap = await getDocs(q);
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            const matchesSearch =
                u.name?.toLowerCase().includes(search.toLowerCase()) ||
                u.email?.toLowerCase().includes(search.toLowerCase()) ||
                u.mobile?.toLowerCase()?.includes(search.toLowerCase());

            const matchesRole =
                roleFilter === "All" || u.role === roleFilter;

            return matchesSearch && matchesRole;
        });
    }, [users, search, roleFilter]);


    const paginatedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const saveUser = async () => {
        if (!form.email || !form.name) return Swal.fire("Error", "Required fields missing", "error");
        try {
            let uid = editingId;
            if (!editingId) {
                const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
                uid = userCredential.user.uid;
            }
            await setDoc(doc(db, "users", uid), {
                uid, ...form, schoolId,
                createdTime: editingId ? form.createdTime : Timestamp.now(),
            });
            toastSuccess(editingId ? "User Updated" : "User Created");
            setForm({ name: "", email: "", mobile: "", password: "", role: "Teacher", status: "Active" });
            setEditingId(null);
            loadUsers();
        } catch (error) { Swal.fire("Error", error.message, "error"); }
    };

    const toastSuccess = (msg) => {
        const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        Toast.fire({ icon: 'success', title: msg });
    };

    const deleteUser = async (uid) => {
        const res = await Swal.fire({
            title: "Are you sure?",
            text: "This user will be permanently removed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d32f2f",
        });
        if (res.isConfirmed) {
            await deleteDoc(doc(db, "users", uid));
            loadUsers();
            Swal.fire("Deleted", "User has been removed.", "success");
        }
    };

    return (
        <Box bgcolor="#f0f2f5" minHeight="100vh">
            <Grid className="row" spacing={2}>
                {/* Left Side: Compact Form */}
                <Grid item xs={12} className="col-sm-4">
                    <Card sx={{ borderRadius: 3, position: 'sticky', top: 16 }}>
                        <Box p={2} borderBottom="1px solid #eee" display="flex" alignItems="center" gap={1}>
                            <PersonAddAlt1 color="primary" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={700}>{editingId ? "Edit User" : "Add User"}</Typography>
                        </Box>
                        <Box p={2}>
                            <Stack spacing={1.5}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Role</InputLabel>
                                    <Select name="role" value={form.role} label="Role" onChange={handleChange}>
                                        {ROLE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField size="small" fullWidth label="Name" name="name" value={form.name} onChange={handleChange} />
                                <TextField size="small" fullWidth label="Email" name="email" value={form.email} onChange={handleChange} />
                                <TextField size="small" fullWidth label="Mobile" name="mobile" value={form.mobile} onChange={handleChange} />
                                {!editingId && <TextField size="small" type="password" fullWidth label="Password" name="password" value={form.password} onChange={handleChange} />}
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Account Status"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}

                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Deactive">Deactive</MenuItem>
                                </TextField>
                                <Button variant="contained" fullWidth onClick={saveUser} sx={{ mt: 1, textTransform: 'none', borderRadius: 2 }}>
                                    {editingId ? "Update Changes" : "Save User"}
                                </Button>
                                {editingId && <Button size="small" color="inherit" onClick={() => setEditingId(null)}>Cancel</Button>}
                            </Stack>
                        </Box>
                    </Card>
                </Grid>


                {/* Right Side: High-Density Table */}
                <Grid item xs={12} className="col-sm-8">
                    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box p={2} display="flex" justifyContent="space-between" alignItems="center" bgcolor="#fff">
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Group color="action" />
                                <Typography variant="h6" fontWeight={700}>Users List</Typography>
                                <Chip
                                    label={`${filteredUsers.length} ${roleFilter === "All" ? "Users" : roleFilter
                                        }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={roleFilter}
                                        label="Role"
                                        onChange={(e) => {
                                            setRoleFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value="All">All Roles</MenuItem>

                                        {ROLE_OPTIONS.map((role) => (
                                            <MenuItem key={role} value={role}>
                                                {role}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    size="small"
                                    placeholder="Quick search..."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                    sx={{ width: 250 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Table size="small">
                            <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                                <TableRow>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>USER</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>CONTACT</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>ROLE</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>STATUS</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedUsers.map((u) => (
                                    <TableRow key={u.uid} hover sx={{ '& td': { py: 0.5 } }}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#5c6bc0' }}>{u.name?.charAt(0)}</Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{u.name}</Typography>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: -0.5 }}>{u.email}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{u.mobile || '—'}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#3949ab', bgcolor: '#e8eaf6', px: 1, py: 0.2, borderRadius: 1 }}>
                                                {u.role}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: u.status === "Active" ? "#4caf50" : "#9e9e9e" }} />
                                                <Typography variant="caption">{u.status}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setForm(u); setEditingId(u.uid); }}><EditOutlined fontSize="inherit" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteUser(u.uid)}><DeleteOutline fontSize="inherit" /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {filteredUsers.length === 0 && (
                            <Box p={4} textAlign="center"><Typography variant="body2" color="textSecondary">No users found.</Typography></Box>
                        )}

                        <Box p={1.5} display="flex" justifyContent="center" borderTop="1px solid #eee" bgcolor="#fff">
                            <Pagination
                                count={Math.ceil(filteredUsers.length / PAGE_SIZE)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                size="small"
                                color="primary"
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Usersmgmt;